import { Box, Icon, Typography, useTheme } from "@outschool/backpack";
import { EnrollmentInput } from "@outschool/gql-backend-generated";
import {
  ActivityEnrollQueryQuery,
  CreditAccountState,
  CurrentSubscriptionStatus,
  EnrollmentMeetingPurchaseQueryQuery,
} from "@outschool/gql-frontend-generated";
import { faExclamationTriangle } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { Loading } from "@outschool/ui-legacy-component-library";
import { useDimensions } from "@outschool/ui-utils";
import React from "react";

import * as Enrollment from "../../../../shared/Enrollment";
import { useSubscriptionCreditBalance } from "../../../hooks/useSubscriptionCreditBalance";
import { CoinIcon } from "../../subscriptions/CoinIcon";
import { useOrderContext } from "../OrderContext";
import { PriceAdjustment } from "./priceBreakdown/LoadedPriceBreakdown";
import { PriceDisplay } from "./priceBreakdown/PriceDisplay";
import RefundPolicy from "./RefundPolicy";
import SubscriptionDisclaimer from "./SubscriptionDisclaimer";

export default function PriceCreditsBreakdown({
  activity,
  canPayLater = false,
  oneOnOneNextBillingDate,
}: {
  canPayLater?: boolean;
  oneOnOneNextBillingDate?: Date;
  activity:
    | NonNullable<ActivityEnrollQueryQuery["enrollmentForm"]>["activity"]
    | NonNullable<
        EnrollmentMeetingPurchaseQueryQuery["enrollmentMeetingPurchaseForm"]
      >["activity"];
}) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\priceCreditsBreakdown",
  );
  const theme = useTheme();
  const boxRef = React.useRef(null);
  const { height: boxHeight } = useDimensions(boxRef?.current ?? undefined);
  const orderContext = useOrderContext();
  const { currentStatus, availableCredits } = useSubscriptionCreditBalance();
  const {
    pricingBreakdown,
    isPricingBreakdownLoading,
    isWeeklyPayment,
    enrollmentInputs,
    promoCode,
  } = orderContext;
  const subscriptionOutstandingBalanceCredits =
    pricingBreakdown?.subscriptionOutstandingBalanceCredits;
  const isOngoing = !!activity.is_ongoing_weekly;
  const isOneOnOne = !!activity.hasTeacherSchedule;

  const promoCodeUpper = promoCode?.toUpperCase();

  const periodText = isOneOnOne
    ? t(" per session")
    : isOngoing || isWeeklyPayment
    ? t(" weekly")
    : activity.isClub
    ? t(" monthly")
    : "";

  if (!pricingBreakdown) {
    return <Loading />;
  }

  const nonGiftEnrollmentInputs = enrollmentInputs.filter(e => !e.isGift);
  const shouldShowUpfrontDiscount =
    !!enrollmentInputs &&
    !!pricingBreakdown &&
    !!pricingBreakdown.perEnrollmentUpfrontCents &&
    !!pricingBreakdown.perEnrollmentWeeklyCents &&
    !isWeeklyPayment &&
    !!activity.pricingInfo &&
    activity.pricingInfo.upfrontDiscountPercentage > 0;

  const hasCoupon =
    Boolean(pricingBreakdown?.couponCents) && pricingBreakdown?.couponCredits;

  // Use explicit nullish check to allow 0 as a valid value for totalCredits
  const subtotalCredits =
    pricingBreakdown.totalCredits != null
      ? pricingBreakdown.totalCredits - (pricingBreakdown.couponCredits ?? 0)
      : null;

  const isEsaUserPayingWithSubscription =
    pricingBreakdown.esaMembershipAccountState ===
      CreditAccountState.InvalidClassNotApprovedForProgram &&
    currentStatus === CurrentSubscriptionStatus.Active &&
    !!availableCredits;

  const styles = {
    container: {
      padding: 8,
      borderRadius: 16,
      marginTop: 10,
      background: theme.palette.primary[50],
    },
    row: {
      display: "flex",
      justifyContent: "space-between",
    },
  };

  return (
    <>
      <Box
        sx={{
          ...styles.container,
        }}
      >
        {isPricingBreakdownLoading && !pricingBreakdown ? (
          <Loading />
        ) : (
          <Box>
            <Box sx={{ padding: "0.5em", paddingTop: "1em" }}>
              {!isOneOnOne ? (
                <Box
                  sx={{
                    paddingBottom: 14,
                    borderBottom: "1px solid",
                    borderColor: "neutral.light",
                    marginBottom: 14,
                  }}
                >
                  {nonGiftEnrollmentInputs.map((enrollmentInput, index) => (
                    <PriceCreditsBreakdownEnrollment
                      key={enrollmentInput.learner?.uid || index}
                      enrollmentInput={enrollmentInput}
                      periodText={periodText}
                      pricingBreakdown={pricingBreakdown}
                      isWeeklyPayment={isWeeklyPayment}
                      shouldShowUpfrontDiscount={shouldShowUpfrontDiscount}
                    />
                  ))}
                  {shouldShowUpfrontDiscount && (
                    <Box
                      sx={{
                        color: "success.600",
                      }}
                      data-test-id="upfront-discount"
                    >
                      <Box
                        flex
                        sx={{
                          justifyContent: "space-between",
                          alignItems: "center",
                          paddingTop: "0.25em",
                        }}
                        data-test-id="upfront-discount-adjustment"
                      >
                        <Box>
                          {t("Upfront payment discount ({{percentageOff}}%)", {
                            percentageOff:
                              activity.pricingInfo!.upfrontDiscountPercentage,
                          })}
                        </Box>
                        <PriceDisplay
                          amountCredits={
                            pricingBreakdown.perEnrollmentUpfrontCredits! *
                              enrollmentInputs.length -
                            pricingBreakdown.perEnrollmentTotalWeeklyCredits! *
                              enrollmentInputs.length
                          }
                        />
                      </Box>
                    </Box>
                  )}
                  {hasCoupon && (
                    <Box
                      flex
                      sx={{
                        color: "success.600",
                        justifyContent: "space-between",
                      }}
                      data-test-id="coupon-adjustment"
                    >
                      <PriceAdjustment
                        label={t('"{{promoCode}}" promo:', {
                          promoCode: promoCodeUpper,
                        })}
                        adjustmentCredits={-pricingBreakdown.couponCredits!}
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                // For one on one, only show price breakdown if we have a coupon
                <Box>
                  {hasCoupon && enrollmentInputs.length > 0 && (
                    <Box
                      sx={{
                        paddingBottom: 14,
                        borderBottom: "1px solid",
                        borderColor: "neutral.light",
                        marginBottom: 14,
                      }}
                    >
                      <PriceCreditsBreakdownEnrollment
                        enrollmentInput={enrollmentInputs[0]}
                        periodText={periodText}
                        pricingBreakdown={pricingBreakdown}
                        isWeeklyPayment={isWeeklyPayment}
                        shouldShowUpfrontDiscount={false}
                        isOneOnOne={true}
                      />
                      <Box
                        flex
                        sx={{
                          color: "#29a877",
                          justifyContent: "space-between",
                        }}
                        data-test-id="coupon-adjustment"
                      >
                        <PriceAdjustment
                          label={t('"{{promoCode}}" promo for 1 session', {
                            promoCode: promoCodeUpper,
                          })}
                          adjustmentCredits={-pricingBreakdown.couponCredits!}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={styles.row}>
                <Typography>
                  <b>{t("Total due today")}</b>
                </Typography>
                <Box
                  as={"span"}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-block",
                      marginRight: 4,
                      width: "20px",
                      height: "20px",
                    }}
                  >
                    <CoinIcon
                      fillColor={theme.palette.warning.main}
                      strokeColor={theme.palette.warning[700]}
                      width={20}
                      height={20}
                    />
                  </Box>{" "}
                  <Typography
                    variant="h5"
                    sx={{ display: "inline-block", marginRight: 4 }}
                  >
                    <b>{canPayLater ? 0 : subtotalCredits} </b>
                  </Typography>{" "}
                  {t("credits")}
                </Box>
              </Box>
              {isEsaUserPayingWithSubscription && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 4,
                    alignItems: "start",
                    justifyContent: "right",
                    marginTop: 8,
                  }}
                >
                  <Icon
                    icon={faExclamationTriangle}
                    sx={{ color: "info.700", fontSize: "1.4rem", marginTop: 3 }}
                  />
                  <Typography variant="body2" sx={{ color: "neutral.600" }}>
                    {t(
                      "We’ll use membership credits—this class isn’t covered by your program",
                    )}
                  </Typography>
                </Box>
              )}
              {!canPayLater &&
                !!subscriptionOutstandingBalanceCredits &&
                subscriptionOutstandingBalanceCredits > 0 && (
                  <Box
                    sx={{
                      ...styles.row,
                      ...{ paddingBottom: 0, paddingTop: 14 },
                    }}
                  >
                    <Typography>{t("Outstanding balance")}</Typography>
                    <Typography
                      sx={{
                        color: "error.600",
                      }}
                    >
                      {subscriptionOutstandingBalanceCredits} {t("credits")}
                    </Typography>
                  </Box>
                )}
            </Box>
            {Boolean(pricingBreakdown?.totalCredits) && (
              <SubscriptionDisclaimer
                activity={activity}
                isWeeklyPayment={isWeeklyPayment}
                oneOnOneNextBillingDate={oneOnOneNextBillingDate}
                canPayLater={canPayLater}
                isSubscriptionCreditPurchase={true}
              />
            )}
          </Box>
        )}
      </Box>

      {!activity.isIncludedWithMembership && (
        <Box
          sx={{
            overflow: "hidden",
            transition: "height 0.5s ease",
            height: boxHeight,
          }}
        >
          <RefundPolicy
            priceCents={activity.priceCredits}
            isSelfPaced={activity.isSelfPaced}
            isClub={activity.isClub}
          />
        </Box>
      )}
    </>
  );
}
type OrderContext = ReturnType<typeof useOrderContext>;
type PriceCreditBreakdownEnrollmentProps = {
  enrollmentInput: EnrollmentInput;
  periodText: string;
  pricingBreakdown: OrderContext["pricingBreakdown"];
  isWeeklyPayment?: boolean;
  shouldShowUpfrontDiscount?: boolean;
  isOneOnOne?: boolean;
};
const PriceCreditsBreakdownEnrollment = ({
  enrollmentInput,
  periodText,
  pricingBreakdown,
  isWeeklyPayment,
  shouldShowUpfrontDiscount,
  isOneOnOne,
}: PriceCreditBreakdownEnrollmentProps) => {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\priceCreditsBreakdown",
  );
  let amountCredits: number | null = null;
  if (pricingBreakdown) {
    if (isOneOnOne) {
      amountCredits = pricingBreakdown.totalCredits;
    } else if (isWeeklyPayment) {
      amountCredits =
        pricingBreakdown.perEnrollmentWeeklyCredits ??
        pricingBreakdown.perEnrollmentCurrentWeekCredits;
    } else {
      amountCredits = shouldShowUpfrontDiscount
        ? pricingBreakdown.perEnrollmentTotalWeeklyCredits
        : pricingBreakdown.perEnrollmentUpfrontCredits;
    }
  }

  const name =
    Enrollment.displayName(enrollmentInput, enrollmentInput.learner) || "-----";
  const age = Enrollment.displayAge(enrollmentInput, enrollmentInput.learner);
  const displayAge = age ? t("age {{age}}", { age }) : "--";

  return (
    <Box
      flex
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography variant="body1">
        {name} ({displayAge})
      </Typography>
      <Typography variant="body1">
        {amountCredits} {!isWeeklyPayment && t("credits")} {periodText}
      </Typography>
    </Box>
  );
};
